import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { TimeOption, Schedule } from '@/utils/types';
import TimeSlot from '@/models/time-slot';
import { getDayFromDate, parseTimeDifference } from '@/utils/utilities';
import Settings, { SettingNamespace } from '@/utils/settings';
import Vue from 'vue';

export enum OrderRetrievalMethod {
    DELIVERY = 'delivery',
    PICK_UP = 'pick_up',
}

export enum AvailableRetrievalMethods {
    ALL = 'all',
    DELIVERY = 'delivery_only',
    PICK_UP = 'pickup_only',
}

export enum AvailableSchedulingOptions {
    ALL = 'all',
    ASAP_ONLY = 'asap_only',
}

@Module({ name: 'timescheduler', namespaced: true })
export default class TimeSchedulerModule extends VuexModule {
    timeSlots: TimeSlot[] = [];
    asapSlot: TimeSlot | null = null;
    selectedTimeSlot: TimeSlot | null = null;
    selectedTimeOption: TimeOption = TimeOption.ASAP;
    currentTimeSlot: TimeSlot | null = null;
    displayWarning: boolean = false;
    isLoading: boolean = false;
    availableRetrievalMethods: AvailableRetrievalMethods = AvailableRetrievalMethods.ALL;
    retrievalMethod: OrderRetrievalMethod = OrderRetrievalMethod.PICK_UP;
    availableSchedulingOption: AvailableSchedulingOptions = AvailableSchedulingOptions.ASAP_ONLY;
    deliveryTime: number = 0;
    timeModalOpen: boolean = false;
    schedules: Record<OrderRetrievalMethod, Schedule | null> = { [OrderRetrievalMethod.DELIVERY]: null, [OrderRetrievalMethod.PICK_UP]: null };

    @Mutation
    public setSelectedTimeSlot(timeSlot: TimeSlot | null): void {
        this.selectedTimeSlot = timeSlot;
    }

    @Mutation
    public setAsapSlot(timeSlot: TimeSlot | null) {
        this.asapSlot = timeSlot;
        if (this.selectedTimeOption === TimeOption.ASAP) {
            this.selectedTimeSlot = this.asapSlot;
        }
    }

    @Mutation
    public setTimeOption(option: TimeOption): void {
        this.selectedTimeOption = option;
    }

    @Mutation
    public setTimeSlots(timeSlots: TimeSlot[]) {
        this.timeSlots = timeSlots;
    }

    @Mutation
    public setDisplayWarning(displayWarning: boolean) {
        this.displayWarning = displayWarning;
    }

    @Mutation
    public setRetrievalMethod(retrievalMethod: OrderRetrievalMethod) {
        this.retrievalMethod = retrievalMethod;
    }

    @Mutation
    public setDeliveryTime(deliveryTime: number) {
        this.deliveryTime = deliveryTime;
    }

    @Mutation
    public setTimeModalOpen(isOpen: boolean) {
        this.timeModalOpen = isOpen;
    }

    @Mutation
    public setCurrentTimeSlot() {
        const currentSlot = this.timeSlots.find((timeSlot: TimeSlot) => {
            const now = new Date();
            return now >= timeSlot.start() && now < timeSlot.end();
        });
        if (currentSlot) {
            this.currentTimeSlot = currentSlot;
        }
    }

    @Mutation
    public setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @Mutation
    public setAvailableRetrievalMethods(availableRetrievalMethods: AvailableRetrievalMethods) {
        this.availableRetrievalMethods = availableRetrievalMethods;
    }

    @Mutation
    public setAvailableSchedulingOption(availableSchedulingOption: AvailableSchedulingOptions) {
        this.availableSchedulingOption = availableSchedulingOption;
    }

    @Mutation
    public setPickupSchedule(pickupSchedule: Schedule) {
        Vue.set(this.schedules, OrderRetrievalMethod.PICK_UP, pickupSchedule);
    }

    @Mutation
    public setDeliverySchedule(deliverySchedule: Schedule) {
        Vue.set(this.schedules, OrderRetrievalMethod.DELIVERY, deliverySchedule);
    }

    @Action
    public async fetchRetrievalMethods() {
        const settings = await Settings.getInstance();
        const retrievalMethods = settings.get(SettingNamespace.ORDERS, 'retrieval_methods');
        if (retrievalMethods !== undefined) {
            this.setAvailableRetrievalMethods(<AvailableRetrievalMethods>retrievalMethods.value);
            if (this.availableRetrievalMethods === AvailableRetrievalMethods.DELIVERY) {
                this.setRetrievalMethod(OrderRetrievalMethod.DELIVERY);
            } else if (this.availableRetrievalMethods === AvailableRetrievalMethods.PICK_UP) {
                this.setRetrievalMethod(OrderRetrievalMethod.PICK_UP);
            }
        }
    }

    @Action
    public async fetchSchedulingOptions() {
        const settings = await Settings.getInstance();
        const schedulingOptions = settings.get(SettingNamespace.ORDERS, 'scheduling_options');
        if (schedulingOptions !== undefined) {
            this.setAvailableSchedulingOption(<AvailableSchedulingOptions>schedulingOptions.value);
        }
    }

    @Action
    public async fetchDeliveryTime() {
        const settings = await Settings.getInstance();
        const deliveryTime = settings.get(SettingNamespace.ORDERS, 'delivery_time');
        if (deliveryTime !== undefined) {
            this.setDeliveryTime(deliveryTime.value || 0);
        }
    }

    @Action
    public async fetchTimeSlots() {
        this.setIsLoading(true);
        const response = await TimeSlot.all();
        if (response) {
            this.setAsapSlot(response.asap);
            this.setTimeSlots(response.slots);
            this.setDisplayWarning(response.warning);
            this.setCurrentTimeSlot();
            // Replace selectedSlot to update state
            if (this.selectedTimeSlot && this.selectedTimeOption === TimeOption.LATER) {
                const selectedSlot = this.timeSlots.find((timeSlot: TimeSlot) => timeSlot.start().getTime() === this.selectedTimeSlot!.start().getTime());
                if (selectedSlot) {
                    this.setSelectedTimeSlot(selectedSlot);
                }
            }
        }
        this.setIsLoading(false);
    }

    @Action
    public async fetchSchedules() {
        const settings = await Settings.getInstance();
        const day = getDayFromDate(new Date());

        const deliveryScheduleSettings = settings.get(SettingNamespace.OPENINGS, 'delivery_schedule')?.value;
        const deliverySchedule = deliveryScheduleSettings.find((schedule: Schedule) => {
            return schedule.name === day;
        });
        const pickupScheduleSettings = settings.get(SettingNamespace.OPENINGS, 'pickup_schedule')?.value;
        const pickupSchedule = pickupScheduleSettings.find((schedule: Schedule) => {
            return schedule.name === day;
        });
        if (deliverySchedule !== undefined) {
            this.setDeliverySchedule(deliverySchedule);
        }
        if (pickupSchedule !== undefined) {
            this.setPickupSchedule(pickupSchedule);
        }
    }

    get isRetrievalMethodAvailable() {
        return (retrievalMethod: OrderRetrievalMethod = this.retrievalMethod, timeSelection: TimeSlot | null = this.selectedTimeSlot) => {
            const retrievalMethodSchedule = this.schedules[retrievalMethod];
            if (retrievalMethodSchedule) {
                if (retrievalMethodSchedule.isClosed) {
                    return false;
                }
                if (timeSelection) {
                    const [startHours, startMinutes] = retrievalMethodSchedule.start_time.split(':');
                    const [endHours, endMinutes] = retrievalMethodSchedule.end_time.split(':');
                    let startTime = new Date();
                    startTime.setHours(parseInt(startHours), parseInt(startMinutes), 0);
                    let endTime = new Date();
                    endTime.setHours(parseInt(endHours), parseInt(endMinutes), 0);
                    const timeSlotStart = new Date(timeSelection.start().toLocaleString('en-US', { timeZone: 'America/Cayman' })).getTime();
                    const timeSlotEnd = new Date(timeSelection.end().toLocaleString('en-US', { timeZone: 'America/Cayman' })).getTime();
                    const scheduleStart = new Date(startTime.toLocaleString('en-US', { timeZone: 'America/Cayman' })).getTime();
                    const scheduleEnd = new Date(endTime.toLocaleString('en-US', { timeZone: 'America/Cayman' })).getTime();
                    if (timeSlotStart < scheduleStart || timeSlotEnd > scheduleEnd) {
                        return false;
                    }
                }
            }
            return true;
        };
    }

    get isRetrievalMethodClosed() {
        return (retrievalMethod: OrderRetrievalMethod) => {
            const retrievalMethodSchedule = this.schedules[retrievalMethod];
            return retrievalMethodSchedule?.isClosed;
        };
    }

    get asapLabel() {
        return (retrievalMethod: OrderRetrievalMethod = this.retrievalMethod) => {
            if (this.currentTimeSlot && this.asapSlot) {
                const now = new Date();
                const tstart = retrievalMethod == OrderRetrievalMethod.DELIVERY ? new Date(this.asapSlot.start().getTime() + this.deliveryTime * 60 * 1000 - now.getTime()).getTime() : new Date(this.asapSlot.start()).getTime() - now.getTime();
                const tend = retrievalMethod == OrderRetrievalMethod.DELIVERY ? new Date(this.asapSlot.end().getTime() + this.deliveryTime * 60 * 1000 - now.getTime()).getTime() : new Date(this.asapSlot.end()).getTime() - now.getTime();
                const start = parseTimeDifference(tstart, 5);
                const end = parseTimeDifference(tend, 5);
                if (!start.days && !start.hours && !start.minutes) {
                    start.minutes = 5;
                }
                const startTime = start.hours ? start.hours + 'h' + (start.minutes ? start.minutes + 'min' : '') : end.hours ? start.minutes + 'min' : start.minutes;
                const endTime = end.hours ? end.hours + 'h' + (end.minutes ? end.minutes + 'min' : '') : end.minutes + 'min';
                return `ASAP (${startTime} - ${endTime})`;
            }
            return 'ASAP';
        };
    }

    get pickupTime() {
        if (this.selectedTimeSlot && this.currentTimeSlot) {
            if (this.selectedTimeOption === TimeOption.LATER) {
                if (this.retrievalMethod === OrderRetrievalMethod.DELIVERY) {
                    const parsedStart = new Date(this.selectedTimeSlot.start().getTime() + this.deliveryTime * 60 * 1000).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
                    const parsedEnd = new Date(this.selectedTimeSlot.end().getTime() + this.deliveryTime * 60 * 1000).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
                    return parsedStart + ' to ' + parsedEnd;
                }
                return this.selectedTimeSlot.parsedStart + ' to ' + this.selectedTimeSlot.parsedEnd;
            }
            if (this.selectedTimeOption === TimeOption.ASAP) {
                return this.asapLabel();
            }
        }
        return 'Pick up time';
    }
}
